import { useMemo, useState } from 'react';
import { featuredStories, type Story } from '@/mocks/featuredStories';
import Header from './components/Header';
import Hero from './components/Hero';
import Weekly from './components/Weekly';
import Monthly from './components/Monthly';
import Yearly from './components/Yearly';
import Feed from './components/Feed';
import Faq from './components/Faq';
import About from './components/About';
import Cta from './components/Cta';
import Footer from './components/Footer';
import BackToTop from './components/BackToTop';
import Reader from './components/Reader';

export default function Home() {
  const [selected, setSelected] = useState<Story | null>(null);

  const storyMap = useMemo(() => {
    const map = new Map<string, Story>();
    featuredStories.forEach((s) => map.set(s.id, s));
    return map;
  }, []);

  const todayStories = useMemo(
    () => featuredStories.filter((s) => s.windows.includes('today')),
    [],
  );
  const todayLead = useMemo(
    () => todayStories.find((s) => !!s.cover) ?? todayStories[0],
    [todayStories],
  );
  const todayQuick = todayStories.filter((s) => s.id !== todayLead.id);

  const weeklyStories = useMemo(
    () =>
      ['kaipan-zongyi-2026', 'guochan-xiju-dianshi', 'liuxingyuan-shenshi', 'ai-shipin-shencha', 'lianyi-gaoxiao-wu']
        .map((id) => storyMap.get(id))
        .filter((s): s is Story => !!s),
    [storyMap],
  );

  const monthlyStories = useMemo(
    () =>
      ['yanchuhui-yinle-pan', 'liuxingyuan-shenshi', 'lianyi-gaoxiao-wu', 'guochan-xiju-dianshi', 'zhoudian-dianying-qi', 'jingji-ren-nianhuo']
        .map((id) => storyMap.get(id))
        .filter((s): s is Story => !!s),
    [storyMap],
  );

  const yearlyStories = useMemo(
    () =>
      ['wugugua-nianpan', 'yanchuhui-yinle-pan', 'ai-shipin-shencha', 'jingji-ren-nianhuo', 'zongyi-chuhai-re', 'kaipan-zongyi-2026', 'lianyi-gaoxiao-wu']
        .map((id) => storyMap.get(id))
        .filter((s): s is Story => !!s),
    [storyMap],
  );

  const openStory = (s: Story) => setSelected(s);

  return (
    <div id="top" className="bg-background-50 text-foreground-900">
      <h1 className="sr-only">
        必吃大瓜——每日更新的娱乐热闻吃瓜站：今日重磅、本周必看、本月最劲爆与必吃大瓜年度盘点，综艺影视明星行业热闻一网打尽。
      </h1>

      <Header />

      <main>
        <Hero lead={todayLead} quick={todayQuick} onOpen={openStory} />

        {/* 栏目快捷导览条 */}
        <nav className="mx-auto mt-2 w-full max-w-6xl px-4 md:px-6" aria-label="栏目快捷导航">
          <div className="grid grid-cols-2 gap-3 rounded-2xl bg-background-100 p-4 sm:grid-cols-4">
            {[
              { href: '#weekly', icon: 'ri-calendar-check-line', t: '本周必看', d: '编辑部周更精选' },
              { href: '#monthly', icon: 'ri-group-line', t: '本月最劲爆', d: '月度爆款雷达' },
              { href: '#yearly', icon: 'ri-trophy-line', t: '年度盘点', d: '2026 回顾专题' },
              { href: '#feed', icon: 'ri-news-line', t: '全量吃瓜流', d: '每日滚动更新' },
            ].map((c) => (
              <a
                key={c.href}
                href={c.href}
                className="group flex items-center gap-3 rounded-xl bg-background-50 p-3.5 transition hover:-translate-y-0.5 hover:bg-primary-500"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-foreground-950 text-accent-400 transition group-hover:bg-background-50/20 group-hover:text-background-50">
                  <i className={`${c.icon} text-lg`} />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-bold text-foreground-900 transition group-hover:text-background-50">
                    {c.t}
                  </span>
                  <span className="block truncate text-xs text-foreground-500 transition group-hover:text-background-100">
                    {c.d}
                  </span>
                </span>
              </a>
            ))}
          </div>
        </nav>

        <Weekly stories={weeklyStories} onOpen={openStory} />
        <Monthly stories={monthlyStories} onOpen={openStory} />
        <Yearly stories={yearlyStories} onOpen={openStory} />
        <Feed />
        <Faq />
        <About />
      </main>

      <Cta />
      <Footer />
      <BackToTop />
      <Reader story={selected} onClose={() => setSelected(null)} />
    </div>
  );
}